import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';
import { confidenceLaneSchema } from './intake';

export const runStatuses = [
  'queued',
  'running',
  'waiting_approval',
  'waiting_review',
  'waiting_input',
  'escalated',
  'completed',
  'failed',
  'cancelled',
  'changes_requested',
] as const;
export const runStatusSchema = z.enum(runStatuses);
export type RunStatus = z.infer<typeof runStatusSchema>;

/** Statuses from which a run can be gracefully stopped (i.e. it isn't already terminal). */
export const cancellableRunStatuses = [
  'queued',
  'running',
  'waiting_approval',
  'waiting_review',
  'waiting_input',
  'escalated',
] as const;
export function isCancellableRunStatus(status: string): boolean {
  return (cancellableRunStatuses as readonly string[]).includes(status);
}

export const runStepKinds = [
  'plan',
  'tool_call',
  'tool_result',
  'message',
  'approval',
  'final',
  /** Collaborate mid-run operator message applied into graph context. */
  'user_message',
  'todo_snapshot',
] as const;
export const runStepKindSchema = z.enum(runStepKinds);
export type RunStepKind = z.infer<typeof runStepKindSchema>;

export const runStepSchema = z
  .object({
    id: idSchema,
    runId: idSchema,
    index: z.number().int().nonnegative(),
    kind: runStepKindSchema,
    content: z.record(z.unknown()).default({}),
    tokensIn: z.number().int().nonnegative().default(0),
    tokensOut: z.number().int().nonnegative().default(0),
    costUsdCents: z.number().nonnegative().default(0),
  })
  .merge(timestampsSchema.partial());
export type RunStep = z.infer<typeof runStepSchema>;

export const runSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    agentId: idSchema,
    conversationId: idSchema.optional(),
    /** Parent run when this run was spawned via delegation. */
    parentRunId: idSchema.nullish(),
    /** Prior run this run was resumed from (Epic N). */
    resumedFromRunId: idSchema.nullish(),
    /** Set when an operator requests a graceful stop; cleared once terminal. */
    cancelRequestedAt: z.coerce.date().nullish(),
    /** Trigger that initiated this run, when started via intake. */
    triggerId: idSchema.nullish(),
    /** Resolved execution mode governing this run's review gate. */
    executionMode: z.enum(['draft', 'assist', 'auto']).nullish(),
    /** Temporal workflow id backing this run. */
    workflowId: z.string().optional(),
    status: runStatusSchema.default('queued'),
    /** Current pipeline stage (see stage.ts). Null until the run starts staging. */
    currentStage: z.string().nullish(),
    /** Confidence lane assigned by Triage (Epic J). */
    confidenceLane: confidenceLaneSchema.nullish(),
    /** Taxonomy label key the run was classified into (Epic J). */
    labelKey: z.string().nullish(),
    /** Workflow type the run was classified into (Epic K). */
    workflowTypeKey: z.string().nullish(),
    input: z.string().default(''),
    output: z.string().optional(),
    error: z.string().optional(),
    totalCostUsdCents: z.number().nonnegative().default(0),
  })
  .merge(timestampsSchema);
export type Run = z.infer<typeof runSchema>;

export const startRunSchema = z.object({
  agentId: idSchema,
  input: z.string().min(1),
  conversationId: idSchema.optional(),
  /** Optional workflow type the run is classified into (Epic K); resolves enabled stages + modes. */
  workflowTypeKey: z.string().max(80).optional(),
});
export type StartRun = z.infer<typeof startRunSchema>;

/** Body for resuming a prior run (Epic N); optional extra guidance for the retry. */
export const resumeRunSchema = z.object({
  instructions: z.string().max(4000).optional(),
});
export type ResumeRun = z.infer<typeof resumeRunSchema>;

/** Body for gracefully stopping a run; optional operator-facing reason. */
export const cancelRunSchema = z.object({
  reason: z.string().max(2000).optional(),
});
export type CancelRun = z.infer<typeof cancelRunSchema>;

/** A node in a run's resume chain, for the evidence timeline (Epic N2). */
export interface RunLineageNode {
  id: string;
  status: string;
  relation: 'ancestor' | 'self' | 'descendant';
  resumedFromRunId?: string | null;
  createdAt?: string | Date;
}

export const approvalDecisionSchema = z.object({
  approve: z.boolean(),
  reason: z.string().max(2000).optional(),
  /** 'request_changes' sends feedback back to the agent for rework. */
  action: z.enum(['approve', 'reject', 'request_changes']).optional(),
  /** Structured file-level feedback for code review collaboration. */
  feedback: z.record(z.string()).optional(),
});
export type ApprovalDecision = z.infer<typeof approvalDecisionSchema>;

export const approvalSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    runId: idSchema,
    toolKey: z.string(),
    toolInput: z.record(z.unknown()),
    status: z.enum(['pending', 'approved', 'rejected']).default('pending'),
    decidedBy: idSchema.optional(),
    reason: z.string().optional(),
  })
  .merge(timestampsSchema);
export type Approval = z.infer<typeof approvalSchema>;

/** Approval enriched with its parent run's context, so the UI can group approvals by run/pipeline. */
export interface EnrichedApproval extends Approval {
  /** Root-most run in the delegation chain — used to surface approvals on the Runs request view. */
  rootRunId?: string;
  /** Chat session (conversation) of the root run, when it was started from Collaborate. */
  conversationId?: string;
  run: {
    agentId: string;
    /** The original user request / input that triggered this run. */
    input: string;
    /** Current status of the associated run. */
    status: string;
    /** When the run was created. */
    createdAt: string;
    workspaceId?: string;
  };
  /** Resolved GitHub repo for integration calls — integration config / workspace beats agent guesses. */
  githubRepo?: {
    owner: string;
    repo: string;
    slug: string;
    url: string;
  };
}
